<template>
  <div class="flex items-center mt-4 w-full">
    <div class="text-l-medium text-black-white mb-1 mr-2">{{ $t('lighting.current') }}</div>
    <div class="flex flex-1 items-center gap-2 w-full">
      <div class="min-w-0 rounded-full flex-1 w-full">
        <div
          class="min-w-0 rounded-full flex-1 w-full"
          :class="[hasLinerGradient ? 'h-4.5' : 'h-8', disabled ? 'bg-neutral-dark-disabled' : '']"
          :style="disabled ? undefined : { background: colorIllustration }"
        ></div>
        <RadioGroup
          v-if="hasLinerGradient && !disabled"
          v-model="switchValue"
          class="min-w-0 items-center w-full mt-1"
        >
          <div class="flex w-full justify-between items-center flex-wrap gap-2">
            <RadioGroupItem
              v-for="(item, index) in rgbGroupInfo"
              :key="index"
              :value="item.value"
              :style="item.style"
              class="border-white cursor-pointer"
            />
          </div>
        </RadioGroup>
      </div>
      <div
        v-if="isColorful && !disabled"
        class="w-20 h-8 bg-key rounded-full px-1 text-center text-black-white text-xs line-height-1 flex items-center justify-center"
      >
        <span> {{ $t('lighting.vibrant') }} </span>
      </div>
      <div v-else-if="!isColorful || disabled" class="shrink-0">
        <Button
          color="normal"
          size="sm"
          shape="corner"
          class="py-1.5 px-3 active:bg-key active:text-black-white"
          :class="{ 'bg-neutral-dark-disabled active:bg-neutral-dark-disabled': disabled }"
        >
          <Input
            :model-value="displayHex"
            size="sm"
            type="text"
            :maxlength="HEX_DIGIT_LENGTH + 1"
            :disabled="disabled"
            class="w-14 p-0 text-center text-black-white hover:text-text-glass-hover text-s-medium bg-transparent disabled:bg-transparent disabled:text-black-white hover:ring-0 focus-visible:ring-0"
            @update:model-value="handleDraft"
            @input="handleInput"
            @blur="commitHex"
            @keydown.enter.prevent="handleEnter"
          />
        </Button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
  import type { RGB, RGBGroup } from '@rapoo/device-sdk'

  import { computed, ref, watch } from 'vue'
  import { rgbToHex, hexToRgb } from '@/utils/color.ts'

  import { Button, Input, RadioGroup, RadioGroupItem } from '@rapoo/element'
  import { HEX_DIGIT_LENGTH, padHex, sanitizeHex, syncInputElement } from './colorInputRules'

  const props = withDefaults(
    defineProps<{
      modelValue?: RGB
      rgbGroup?: RGBGroup
      hasLinerGradient?: boolean
      gradientDirection?: number
      isColorful?: boolean
      groupIndex?: number
      /** 无法修改颜色时禁用输入并将色块设为 disabled 色 */
      disabled?: boolean
    }>(),
    {
      modelValue: () => ({ r: 0, g: 0, b: 0 }),
      disabled: false,
    },
  )
  const emit = defineEmits(['update:modelValue', 'change', 'update:groupIndex'])

  const switchValue = computed({
    get() {
      return props.groupIndex
    },
    set(val) {
      emit('update:groupIndex', val)
    },
  })
  const rgbGroupInfo = computed(() => {
    return props.rgbGroup?.map((item, index) => {
      return {
        value: index,
        rgb: item,
        style: {
          background: `rgb(${item.r}, ${item.g}, ${item.b})`,
        },
      }
    })
  })

  /** 输入草稿：编辑中不立刻同步到父级 */
  const hexDraft = ref(rgbToHex(props.modelValue))
  const isEditing = ref(false)

  const hexColor = computed(() => rgbToHex(props.modelValue))
  const displayHex = computed(() => (props.disabled ? '/' : hexDraft.value))

  const colorIllustration = computed(() => {
    if (props.isColorful)
      return 'linear-gradient(to right,hsl(0,100%,50%),hsl(60,100%,50%),hsl(120,100%,50%),hsl(180,100%,50%),hsl(240,100%,50%),hsl(300,100%,50%),hsl(359,100%,50%))'

    if (props?.gradientDirection && [2, 3].includes(props?.gradientDirection) && props.rgbGroup) {
      const [c1, c2] = props.rgbGroup
      const dir = props.gradientDirection === 3 ? 'right' : 'bottom'
      return `linear-gradient(to ${dir}, rgb(${c1.r},${c1.g},${c1.b}), rgb(${c2.r},${c2.g},${c2.b}))`
    }

    return hexColor.value
  })

  watch(
    () => props.modelValue,
    (val) => {
      if (isEditing.value) return
      hexDraft.value = rgbToHex(val)
    },
  )

  const handleDraft = (val: string | number) => {
    if (props.disabled) return
    isEditing.value = true
    hexDraft.value = sanitizeHex(String(val))
  }

  const handleInput = (event: Event) => {
    if (props.disabled) return
    syncInputElement(event, sanitizeHex)
  }

  const isSameRgb = (a: RGB, b: RGB) => a.r === b.r && a.g === b.g && a.b === b.b

  /** 失焦或回车后提交颜色更新，不足 6 位用 f 补足 */
  const commitHex = () => {
    if (props.disabled) return
    isEditing.value = false
    const rgb = hexToRgb(padHex(hexDraft.value))
    hexDraft.value = rgbToHex(rgb)
    if (isSameRgb(rgb, props.modelValue)) return
    emit('update:modelValue', rgb)
    emit('change', rgb)
  }

  const handleEnter = (e: KeyboardEvent) => {
    ;(e.target as HTMLInputElement).blur()
  }
</script>
